import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  Alert,
  AlertTitle,
  AlertDescription,
  VStack,
  AlertIcon,
  useDisclosure,
  useToast,
  Tooltip,
  Badge,
  Icon,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

// Custom components
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlineEye } from 'react-icons/ai'
import { MdCheckCircle, MdAccessTime } from 'react-icons/md'

import { useGetFeedbacks, useDeleteFeedback } from 'queries/feedback.query'
import { DeleteFeedbackModal } from './DeleteFeedbackModal'
import ViewFeedbackModal from './ViewFeedbackModal'
import { CustomPagination } from 'views/admin/communities/components/pagination'

const columnHelper = createColumnHelper()

export default function FeedbackTable () {
  const [sorting, setSorting] = React.useState([])
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.50', 'whiteAlpha.100')

  // Delete Modal state
  const {
    isOpen: isDeleteOpen,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure()
  const [selectedForDeletion, setSelectedForDeletion] = React.useState(null)

  // View / Reply Modal state
  const {
    isOpen: isViewOpen,
    onOpen: onOpenView,
    onClose: onCloseView,
  } = useDisclosure()
  const [selectedFeedback, setSelectedFeedback] = React.useState(null)

  const toast = useToast()
  const { mutate: deleteMutate, isPending } = useDeleteFeedback()

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  })

  const { data, isLoading, isError, refetch } = useGetFeedbacks(
    pagination.current,
    pagination.pageSize,
  )

  const rows = data?.data || []

  const handleOpenView = (feedback) => {
    setSelectedFeedback(feedback)
    onOpenView()
  }

  const handleSingleDelete = (e, id) => {
    e.stopPropagation()
    setSelectedForDeletion(id)
    onOpenDelete()
  }

  const onDelete = () => {
    if (selectedForDeletion) {
      deleteMutate(selectedForDeletion, {
        onSuccess: () => {
          onCloseDelete()
          setSelectedForDeletion(null)
          refetch()
          toast({
            title: 'Feedback Deleted.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
        },
        onError: err => {
          console.error('Delete Failed:', err)
          toast({
            title: 'Failed to Delete Feedback.',
            description: 'There was an error deleting the entry.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
        },
      })
    }
  }

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Sender
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue() || 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Email
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' textAlign='center'>
            {info.getValue() || 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('subject', {
      id: 'subject',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Subject
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' textAlign='center' fontWeight="500">
            {info.getValue() || 'No Subject'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('message', {
      id: 'message',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Message Snippet
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Tooltip label={info.getValue()} placement='top'>
            <Text
              color={textColor}
              fontSize='sm'
              maxW='220px'
              overflow='hidden'
              textOverflow='ellipsis'
              whiteSpace='nowrap'
            >
              {info.getValue()
                ? `${info.getValue().substring(0, 50)}...`
                : 'No Message'}
            </Text>
          </Tooltip>
        </Flex>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Status
        </Text>
      ),
      cell: ({ row }) => {
        const isReplied = Boolean(row.original.reply_message || row.original.status === 'replied')
        return (
          <Flex justifyContent='center' alignItems='center'>
            {isReplied ? (
              <Badge
                colorScheme='green'
                fontSize='xs'
                px={2.5}
                py={0.5}
                borderRadius='full'
                display='flex'
                alignItems='center'
                gap={1}
              >
                <Icon as={MdCheckCircle} /> Replied
              </Badge>
            ) : (
              <Badge
                colorScheme='orange'
                fontSize='xs'
                px={2.5}
                py={0.5}
                borderRadius='full'
                display='flex'
                alignItems='center'
                gap={1}
              >
                <Icon as={MdAccessTime} /> Pending
              </Badge>
            )}
          </Flex>
        )
      },
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Date Received
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          <Text color={textColor} fontSize='sm' textAlign='center'>
            {info.getValue()
              ? new Date(info.getValue()).toLocaleDateString()
              : 'N/A'}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text
          textAlign='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Actions
        </Text>
      ),
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          <ButtonGroup size='sm' spacing={1}>
            <Tooltip label="View Details & Reply via Email">
              <Button
                colorScheme='brand'
                variant='solid'
                size='sm'
                onClick={(e) => {
                  e.stopPropagation()
                  handleOpenView(row.original)
                }}
                leftIcon={<AiOutlineEye />}
              >
                View
              </Button>
            </Tooltip>
            <Tooltip label="Delete Feedback">
              <Button
                colorScheme='red'
                variant='ghost'
                size='sm'
                onClick={(e) => handleSingleDelete(e, row.original.id)}
                isLoading={isPending && selectedForDeletion === row.original.id}
              >
                <AiOutlineDelete />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Flex>
      ),
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) return <SkeletonTable />
  if (isError || !data) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle' borderRadius="xl">
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load feedback data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button onClick={refetch} colorScheme='red' isLoading={isLoading}>
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='16px' justifyContent='space-between' align='center'>
        <Box>
          <Text
            color={textColor}
            fontSize='22px'
            fontWeight='700'
            lineHeight='100%'
          >
            User Feedbacks
          </Text>
          <Text fontSize="xs" color="gray.400" mt={1}>
            Click on any row or the &ldquo;View&rdquo; button to read the full message and send an official email reply directly from the site&apos;s email address.
          </Text>
        </Box>
      </Flex>
      <Box
        maxH='600px'
        overflowY='auto'
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#999 #ddd' }}
      >
        <Table color='gray.500' mb='24px' mt='12px'>
          <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    borderColor={borderColor}
                    textAlign='center'
                  >
                    <Flex
                      justifyContent='center'
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr
                key={row.id}
                cursor='pointer'
                _hover={{ bg: hoverBackgroundColor }}
                onClick={() => handleOpenView(row.original)}
              >
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* PAGINATION BLOCK */}
      {data && data.total > 0 && (
        <CustomPagination
          total={data.total}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onPageChange={page =>
            setPagination(prev => ({ ...prev, current: page }))
          }
          onPageSizeChange={size =>
            setPagination(prev => ({ ...prev, pageSize: size, current: 1 }))
          }
        />
      )}

      {/* View & Reply Modal */}
      <ViewFeedbackModal
        isOpen={isViewOpen}
        onClose={onCloseView}
        feedback={selectedFeedback}
        onReplySuccess={refetch}
      />

      {/* Delete Confirmation Modal */}
      <DeleteFeedbackModal
        isOpen={isDeleteOpen}
        onClose={onCloseDelete}
        onDelete={onDelete}
        isLoading={isPending}
        feedbackId={selectedForDeletion}
      />
    </Card>
  )
}
